import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import * as React from 'react';
import type { SortDir } from '../hooks/use-table-view';
import { cn } from '../utils';
import { TableHead } from './table';

export interface SortableTableHeadProps
  extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'onClick'> {
  /** This column's sort key. */
  sortKey: string;
  /** Current direction if this column is the active sort, else `null`. */
  direction: SortDir | null;
  /** Toggles the sort for this column. */
  onSort: (key: string) => void;
  children: React.ReactNode;
}

/**
 * A `TableHead` whose label is a button that cycles the column's sort. Shows an
 * up/down arrow when active and a neutral up-down glyph otherwise, and sets
 * `aria-sort` so assistive tech announces the current order.
 */
export const SortableTableHead = React.forwardRef<
  HTMLTableCellElement,
  SortableTableHeadProps
>(({ sortKey, direction, onSort, children, className, ...props }, ref) => {
  const Icon =
    direction === 'asc'
      ? ArrowUp
      : direction === 'desc'
        ? ArrowDown
        : ArrowUpDown;
  return (
    <TableHead
      ref={ref}
      aria-sort={
        direction === 'asc'
          ? 'ascending'
          : direction === 'desc'
            ? 'descending'
            : 'none'
      }
      className={className}
      {...props}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'hover:text-foreground -ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
          direction && 'text-foreground',
        )}
      >
        {children}
        <Icon className={cn('size-3.5 shrink-0', !direction && 'opacity-50')} />
      </button>
    </TableHead>
  );
});
SortableTableHead.displayName = 'SortableTableHead';
